// Drawer Team uses Participants matrix vocabulary and supplied participant
// evidence only. Scalar ownership is deliberately not synchronized. Loading / failed /
// unavailable reads stay distinct from confirmed-empty slots and never count
// toward "{n} roles unassigned".
import { T } from "../../../app/tokens.js";
import { InternalLink } from "../../../components/InternalLink.jsx";
import { cardHead, cardTitle } from "./ProjectSummaryCard.jsx";
import { MATRIX_COLUMNS, cellPeople, initials } from "../hub/peopleRolesModel.js";

const linkStyle = { border: 0, background: "transparent", padding: 0, color: T.blue, fontSize: T.fsXs, fontWeight: T.fwBold, cursor: "pointer", textDecoration: "none", fontFamily: "inherit" };
const slotLabel = { fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".06em", textTransform: "uppercase", color: T.mid, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

function Avatar({ name, tone }) {
  if (!name) {
    return <span aria-hidden="true" data-testid="team-avatar-empty" style={{ width: 26, height: 26, flex: "0 0 26px", borderRadius: "50%", border: `1.5px dashed ${T.light}`, color: T.light, display: "inline-grid", placeItems: "center", fontSize: T.fsXs, fontWeight: T.fwBold, boxSizing: "border-box" }}>?</span>;
  }
  return <span aria-hidden="true" style={{ width: 26, height: 26, flex: "0 0 26px", borderRadius: "50%", background: tone, color: T.white, display: "inline-grid", placeItems: "center", fontSize: T.fsXxs, fontWeight: T.fwBold }}>{initials(name)}</span>;
}

function Person({ name, tone, primary = false, testId }) {
  return (
    <div data-testid={testId} style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
      <Avatar name={name} tone={tone} />
      {name
        ? <span style={{ fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.dark, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={name}>
            {name}{primary && <span style={{ marginLeft: 5, fontSize: T.fsXxs, color: T.mid, fontWeight: T.fwSemibold }}>primary</span>}
          </span>
        : <span style={{ fontSize: T.fsSm, color: T.mid }}>Unassigned</span>}
    </div>
  );
}

function Slot({ label, people, tone, testId, compact = false }) {
  return (
    <div data-testid={testId} data-empty={people.length ? undefined : "true"}
      style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 6, ...(compact ? { border: `1px solid ${T.border}`, borderRadius: 10, padding: "9px 11px", background: T.hoverBg } : null) }}>
      <div style={slotLabel}>{label}</div>
      {people.length
        ? people.map((p, i) => p.unnamed
          ? <div key={p.key || i} data-testid={`${testId}-name-unavailable`} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: T.fsSm, color: T.mid, fontStyle: "italic" }}><Avatar name="?" tone={T.light} />Assigned · name unavailable</div>
          : <Person key={p.key || `${p.name}-${i}`} name={p.name} primary={p.primary} tone={tone} testId={`${testId}-person`} />)
        : <Person name={null} testId={`${testId}-unassigned`} />}
    </div>
  );
}

// Assigned rows without a readable name stay assigned (identity unavailable);
// they are never shown or counted as Unassigned.
const fromSlot = (slot) => (slot?.people || []).map((p) => {
  const raw = p.displayName || p.name;
  const name = raw && String(raw).trim() ? String(raw).trim() : null;
  return { key: p.personId || p.id, name, unnamed: !name, primary: Boolean(p.isPrimary) };
});

/** Pure: Team projection. Unknown evidence is never counted as unassigned. */
export function teamModel(project, participation) {
  const st = participation || { status: "unavailable" };
  const part = st.status === "ok" && st.part?.available ? st.part : null;
  const participants = part ? part.slots.flatMap(s => s.people) : [];
  const cells = part ? MATRIX_COLUMNS.map(c => ({
    key: c.id, label: c.label, ownership: c.key === null,
    people: fromSlot({ people: cellPeople(participants, c) }),
  })) : [];
  return {
    ownership: cells.filter(c => c.ownership), tracks: cells.filter(c => !c.ownership),
    evidence: part ? "ok" : st.status === "ok" ? "unavailable" : st.status,
    error: st.error, onRetry: st.onRetry, reason: st.reason,
    unassigned: cells.filter(c => !c.people.length).length,
  };
}

export function ProjectTeam({ project, participation, role = "viewer", participantsHref, frozen = false }) {
  const m = teamModel(project, participation);
  const isAdmin = role === "admin";
  return (
    <section data-testid="record-team" aria-labelledby="record-team-title" style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, minWidth: 0, marginBottom: 12 }}>
      <div style={cardHead}><h2 id="record-team-title" style={cardTitle}>Team</h2>
        {frozen && m.evidence === "ok" && <span style={{ fontSize: T.fsXxs, color: T.mid }}>Selected-week participants · captured archive</span>}
      </div>
      <div style={{ padding: "10px 18px 14px" }}>
        <div data-testid="team-roles" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
          {m.ownership.map(s => <Slot key={s.key} testId={`team-${s.key}`} label={s.label} tone={s.key === "delivery_owner" ? T.blue : T.ragAmberText} people={s.people} />)}
        </div>
        <div style={{ ...slotLabel, margin: "14px 0 6px" }}>Tracks</div>
        {m.evidence === "ok" ? (
          <div data-testid="team-tracks" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8 }}>
            {m.tracks.map((t) => <Slot key={t.key} compact testId={`team-track-${t.key}`} label={t.label} tone={T.projectsHubNavy} people={t.people} />)}
          </div>
        ) : m.evidence === "loading" ? (
          <div data-testid="team-tracks-loading" aria-busy="true" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8 }}>
            {[0, 1, 2, 3].map((i) => <div key={i} style={{ height: 58, borderRadius: 10, background: T.lightGray }} />)}
          </div>
        ) : m.evidence === "error" ? (
          <div role="alert" data-testid="team-tracks-error" style={{ display: "flex", alignItems: "center", gap: 10, fontSize: T.fsSm, color: T.mid }}>
            <span style={{ flex: 1 }}>Project participants are unavailable: {m.error || "the participant read failed."}</span>
            {m.onRetry && <button type="button" data-testid="team-tracks-retry" onClick={m.onRetry} style={{ border: `1px solid ${T.border}`, background: T.card, borderRadius: 8, padding: "4px 10px", fontSize: T.fsXs, fontWeight: T.fwBold, cursor: "pointer" }}>Retry</button>}
          </div>
        ) : (
          <div data-testid="team-tracks-unavailable" style={{ fontSize: T.fsSm, color: T.mid, fontStyle: "italic" }}>
            {m.reason || (frozen ? "Participants were not captured with this archive." : "Project participants are unavailable for this project.")}
          </div>
        )}
        {m.unassigned > 0 && (
          <div data-testid="team-unassigned" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.border}`, fontSize: T.fsXs, color: T.ragAmberText, fontWeight: T.fwSemibold }}>
            <span data-testid="team-unassigned-count">{m.unassigned} {m.unassigned === 1 ? "role" : "roles"} unassigned</span>
            {isAdmin && participantsHref ? (
              <><span aria-hidden="true">·</span><InternalLink href={participantsHref} returnRecord={{ kind: "project", label: project?.name || "Project" }} data-testid="team-assign-admin" style={linkStyle}>Assign <span aria-hidden="true">&rarr;</span></InternalLink></>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
}
